import { ethers } from "ethers";
import { EVM_CONFIG } from "../config";
import type { SpendingKeys, StoredJoinSplitNote } from "../joinsplit/types";
import {
  requestPoolRelayQuote,
  type PoolConsolidationQuoteBody,
} from "../utils/relayer-client";
import { assertNotAborted } from "./common";
import {
  planPairwiseConsolidation,
  type DepositMergeScope,
} from "./depositMergePlan";
import { candidateNotes } from "./noteCandidates";
import { authenticateAndDedupeCandidates } from "./noteSelection";
import { createFlatJoinSplitV2Note, flatFieldHex } from "./notes";
import type { OperationLog } from "./operationTypes";
import { reservedFlatJoinSplitV2Commitments } from "./pendingJournal";
import { usesPrivateFees } from "./privateFees";
import { proveAndSend } from "./proveAndSend";
import { withReadCancellation } from "./readCancellation";

export function assertConsolidationSponsorship(
  quote: unknown,
  request: PoolConsolidationQuoteBody,
): void {
  const q = quote as {
    version?: number;
    network?: string;
    request?: PoolConsolidationQuoteBody;
    sponsored?: boolean;
    fee?: string;
    minimumFee?: string;
    feeRecipient?: string;
    issuedAt?: number;
    expiresAt?: number;
  } | null;
  const r = q?.request;
  const sameAddress = (a: unknown, b: string) =>
    typeof a === "string" &&
    ethers.isAddress(a) &&
    ethers.getAddress(a) === ethers.getAddress(b);
  if (
    !q ||
    q.version !== 1 ||
    q.network !== EVM_CONFIG.NETWORK?.key ||
    q.sponsored !== true ||
    q.fee !== "0" ||
    q.minimumFee !== "0" ||
    typeof q.feeRecipient !== "string" ||
    !ethers.isAddress(q.feeRecipient) ||
    !r ||
    r.chainId !== request.chainId ||
    !sameAddress(r.pool, request.pool) ||
    r.kind !== "joinsplit" ||
    !sameAddress(r.token, request.token) ||
    r.inputCount !== 2 ||
    r.outputCount !== 1 ||
    r.treeIndex !== request.treeIndex ||
    !Number.isSafeInteger(q.issuedAt) ||
    !Number.isSafeInteger(q.expiresAt) ||
    q.issuedAt! > Date.now() + 5_000 ||
    q.expiresAt! <= Date.now() ||
    q.expiresAt! <= q.issuedAt! ||
    q.expiresAt! - q.issuedAt! > 120_000
  ) {
    throw new Error(
      "Automatic consolidation requires a matching, fully sponsored quote",
    );
  }
}

/** One exact pair, one owner output, ordinary 2x2 proof, no wallet fallback. */
export async function executeEvmConsolidation(params: {
  signer: ethers.Signer;
  provider: ethers.Provider;
  chainId: bigint;
  pool: string;
  token: string;
  notes: StoredJoinSplitNote[];
  spendingKeys: SpendingKeys;
  signal?: AbortSignal;
  onLog: OperationLog;
}) {
  assertNotAborted(params.signal, "before automatic consolidation");
  const pool = ethers.getAddress(params.pool);
  const token = ethers.getAddress(params.token);
  if (usesPrivateFees(pool)) return null;
  const contract = new ethers.Contract(
    pool,
    [
      "function currentTreeIndex() view returns(uint256)",
      "function currentTokenHash(address token) view returns(bytes32)",
    ],
    params.provider,
  );
  const [tree, hash, accountAddress] = await withReadCancellation(
    () =>
      Promise.all([
        contract.currentTreeIndex(),
        contract.currentTokenHash(token),
        params.signer.getAddress(),
      ]),
    params.signal,
  );
  const treeIndex = Number(tree);
  if (!Number.isSafeInteger(treeIndex) || treeIndex < 0)
    throw new Error("Invalid consolidation tree");
  const scope: DepositMergeScope = {
    network: `evm:${params.chainId}`,
    pool,
    treeIndex,
    tokenHash: BigInt(hash),
  };
  const reserved = await reservedFlatJoinSplitV2Commitments({
    chainId: params.chainId,
    poolAddress: pool,
    accountAddress,
    spendingKey: params.spendingKeys.spendingKey,
  });
  const authenticated = await withReadCancellation(
    () =>
      authenticateAndDedupeCandidates({
        candidates: candidateNotes(params.notes, pool, params.chainId, token),
        pool,
        chainId: params.chainId,
        tokenAddress: token,
        spendingKeys: params.spendingKeys,
      }),
    params.signal,
  );
  const candidates = authenticated.map((stored) => ({
    stored,
    scope,
    confirmed: true,
    reserved: reserved.has(stored.note.commitment.toLowerCase()),
  }));
  const selected = planPairwiseConsolidation({
    scope,
    candidates,
    sponsored: true,
    interactiveOperationPending: false,
    outputCount: 1,
  });
  if (!selected) return null;
  const request: PoolConsolidationQuoteBody = {
    chainId: Number(params.chainId),
    pool,
    kind: "joinsplit",
    token,
    treeIndex,
    inputCount: 2,
    outputCount: 1,
  };
  const requireSponsorship = async () => {
    assertNotAborted(params.signal, "before checking sponsorship");
    const quote = await withReadCancellation(
      () => requestPoolRelayQuote(request, { signal: params.signal }),
      params.signal,
    );
    assertConsolidationSponsorship(quote, request);
    assertNotAborted(params.signal, "after checking sponsorship");
  };
  await requireSponsorship();
  const output = await createFlatJoinSplitV2Note({
    recipientMpk: params.spendingKeys.mpk,
    chainId: params.chainId,
    poolAddress: pool,
    treeIndex,
    tokenAddress: token,
    tokenHash: hash,
    value: selected[0].note.value + selected[1].note.value,
  });
  const result = await proveAndSend({
    ...params,
    pool,
    tokenAddress: token,
    selected: [...selected],
    outputs: [output],
    kind: "joinsplit",
    fee: 0n,
    relay: true,
    confirmFallback: () => false,
    beforeDisclosure: requireSponsorship,
    expectedOutputTreeIndex: treeIndex,
    expectedOutputTokenHash: hash,
    deliveries: [
      {
        commitment: output.commitment,
        recipientMpkHex: flatFieldHex(params.spendingKeys.mpk),
        keepLocal: true,
      },
    ],
  });
  return {
    ...result,
    spentCommitments: selected.map(({ note }) => note.commitment),
  };
}
